/**
 * `parseEmbed` — resolve the embed identity from the iframe app's own URL
 * (ARCHITECTURE §7). The loader sets the iframe `src` to
 * `${APP_ORIGIN}/?cid=ORG_ID-WIDGET_ID` (+ optional `&ctx=` commerce context);
 * this reads it back inside the app.
 *
 * The `cid` (`ORG_ID-WIDGET_ID`) is exactly the scoping key the public widget
 * API expects (see `@flozic/shared` `api.widget` + `endpoints.widgetPublic`).
 */

/** Parsed embed identity, derived from `?cid=` (+ `&ctx=` + `&loc=`). */
export interface EmbedContext {
  /** `ORG_ID-WIDGET_ID` — passed verbatim to the public widget API. */
  cid: string;
  orgId: string;
  widgetId: string;
  /** Optional commerce/page context (decoded from `&ctx=` JSON). */
  context: Record<string, unknown> | null;
  /**
   * The HOST page's full URL, forwarded by the loader (`&loc=`). document.referrer inside the
   * cross-origin iframe is origin-only under the default Referrer-Policy, so this is the reliable
   * source for url_contains trigger matching + accurate visitor page context. Null in standalone dev.
   */
  pageUrl: string | null;
}

/** Split a `cid` into its org/widget parts (`ORG-WIDGET`). */
function splitCid(cid: string): { orgId: string; widgetId: string } | null {
  const dash = cid.indexOf('-');
  if (dash <= 0 || dash >= cid.length - 1) return null;
  return { orgId: cid.slice(0, dash), widgetId: cid.slice(dash + 1) };
}

/** Decode the `&ctx=` param (URL-encoded JSON) into an object, tolerating garbage. */
function decodeCtx(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Parse the embed identity from a search string (defaults to the live URL).
 * Falls back to `VITE_DEV_CID` in dev when no `?cid=` is present.
 *
 * @throws if no usable `cid` can be resolved.
 */
export function parseEmbed(search: string = window.location.search): EmbedContext {
  const params = new URLSearchParams(search);
  const cid = params.get('cid') ?? import.meta.env.VITE_DEV_CID ?? '';
  const parts = splitCid(cid);
  if (!parts) {
    throw new Error(
      `[lc-widget] invalid embed id: expected ?cid=ORG_ID-WIDGET_ID, got "${cid}"`,
    );
  }
  return {
    cid,
    orgId: parts.orgId,
    widgetId: parts.widgetId,
    context: decodeCtx(params.get('ctx')),
    pageUrl: params.get('loc'),
  };
}
